import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function BridgePage() {
  // Staggered animation variants
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1] // Custom ease for editorial feel
      }
    }
  };
  return <div className="min-h-screen w-full bg-[#f8f4ed] text-[#2a2a2a] font-sans selection:bg-[#b8943e] selection:text-white overflow-x-hidden" data-id="element-2">
      {/* Font Import */}
      <style data-id="element-3">
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Inter:wght@300;400;500&display=swap');
          
          .font-serif {
            font-family: 'Playfair Display', serif;
          }
          .font-sans {
            font-family: 'Inter', sans-serif;
          }
        `}
      </style>

      <main className="max-w-2xl mx-auto px-6 py-20 md:py-32 flex flex-col items-center" data-id="element-4">
        <motion.article initial="hidden" animate="visible" variants={containerVariants} className="w-full flex flex-col items-center text-center space-y-10 md:space-y-12" data-id="element-5">
          {/* 1. HEADLINE */}
          <motion.h1 variants={itemVariants} className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.2] md:leading-[1.15] text-[#1a1a2e] font-medium max-w-xl" data-id="element-6">
            Cuando haces mucho…
            <span className="block mt-2 md:mt-3 italic text-[#b8943e]/90" data-id="element-7">
              pero la mente sigue activa
            </span>
          </motion.h1>

          {/* 2. PARAGRAPH 1 */}
          <motion.p variants={itemVariants} className="text-lg md:text-xl leading-relaxed text-[#2a2a2a]/90 max-w-lg font-light" data-id="element-8">
            El día avanza, cumples con lo que toca y, cuando todo se calma,
            aparece ese diálogo interno que no siempre sabe cuándo parar.
          </motion.p>

          {/* 3. PARAGRAPH 2 */}
          <motion.p variants={itemVariants} className="text-lg md:text-xl leading-relaxed text-[#2a2a2a]/90 max-w-lg font-light" data-id="element-9">
            No es falta de esfuerzo. Es una forma habitual de procesar
            exigencias, pensamientos y expectativas.
          </motion.p>

          {/* 4. IMAGE */}
          <motion.div variants={itemVariants} className="w-full max-w-lg my-4" data-id="element-10">
            <div className="relative rounded-xl overflow-hidden shadow-xl shadow-[#b8943e]/10 bg-[#eaddcf]" data-id="element-11">
              <img src="https://cdn.magicpatterns.com/uploads/gJRuWHDGgNsY4VoZbZ6HjT/fot_para_la_pagina_puente_1.png" alt="Mujer leyendo la guía Silencia a Mamá Juez en una tablet, en un ambiente cálido y acogedor" className="w-full h-auto object-cover transform transition-transform duration-1000 hover:scale-[1.02]" loading="lazy" data-id="element-12" />
              {/* Subtle overlay for warmth integration */}
              <div className="absolute inset-0 bg-[#b8943e]/5 pointer-events-none mix-blend-multiply" data-id="element-13" />
            </div>
          </motion.div>

          {/* 5. PARAGRAPH 3 */}
          <motion.p variants={itemVariants} className="text-lg md:text-xl leading-relaxed text-[#2a2a2a] max-w-lg font-medium" data-id="element-14">
            Hoy existen enfoques prácticos para entender cómo funciona ese
            diálogo interno, sin pelear contigo ni entrar en procesos largos.
          </motion.p>

          {/* 5b. PARAGRAPH 4 */}
          <motion.p variants={itemVariants} className="text-lg md:text-xl leading-relaxed text-[#2a2a2a]/90 max-w-lg font-light" data-id="element-15">
            Esta guía propone una forma clara y cotidiana de observarlo y
            trabajar con él, desde la calma y el ritmo real.
          </motion.p>

          {/* 6. CTA BUTTON */}
          <motion.div variants={itemVariants} className="pt-4" data-id="element-16">
            <a href="https://mamajuez.narelaspace.com/" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-[#1a1a2e] bg-[#b8943e] rounded-full overflow-hidden transition-all duration-300 hover:bg-[#a68536] hover:shadow-lg hover:shadow-[#b8943e]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#b8943e] focus:ring-offset-[#f8f4ed]" data-id="element-17">
              <span className="relative z-10 flex items-center gap-2" data-id="element-18">
                Conocer cómo funciona
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-19" />
              </span>
              {/* Subtle shine effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent z-0" data-id="element-20" />
            </a>
          </motion.div>

          {/* 7. FOOTER LINE */}
          <motion.div variants={itemVariants} className="pt-8 border-t border-[#2a2a2a]/10 w-full max-w-xs mx-auto" data-id="element-21">
            <p className="text-sm text-[#2a2a2a]/60 font-medium tracking-wide uppercase text-center" data-id="element-22">
              Guía práctica · 10 minutos al día · ritmo real
            </p>
          </motion.div>
        </motion.article>
      </main>
    </div>;
}